/*
 * CanvasChart — canvas 2D replacement for the Highcharts subset used by UI/Graph.js.
 *
 * Implements the same public surface Graph.js consumed from Highcharts so the
 * graph logic ports without structural changes:
 *   - chart: addSeries, get(id), series[], update, redraw, plotTop/plotLeft/plotHeight,
 *     events (click, load, redraw, render)
 *   - xAxis[0]: toPixels, toValue, setExtremes, min/max, series
 *   - xAxis[1] (linked): ticks, minorTicks, label formatter callbacks
 *   - yAxis[0]: toPixels, addPlotLine
 *   - renderer: path/rect/image/label retained-mode facade (attr().add(), destroy()/remove())
 *   - series.points[].update({marker}) for the isolated-point markers
 *
 * Rendering is a display list repainted in full on each redraw; at these data
 * sizes (a few thousand points) a repaint costs well under a frame, which is why
 * canvas 2D was chosen over WebGL.
 */
(function (global, factory) {
    if (typeof module === 'object' && module.exports) {
        // CommonJS / bundlers (Vite resolves this for JWX ESM imports).
        module.exports = factory();
    } else {
        // Legacy gulp bundles: register on the jweather global.
        global.jweather = global.jweather || {};
        global.jweather.UI = global.jweather.UI || {};
        global.jweather.UI.CanvasChart = factory();
    }
})(typeof window !== 'undefined' ? window : this, function () {

    var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var HOUR = 3600 * 1000;
    var DAY = 24 * HOUR;
    // Mobile Safari caps total canvas area (~16.7M px); stay under it when the
    // scrolled chart gets very wide by lowering the backing-store resolution.
    var MAX_CANVAS_AREA = 16000000;

    var imageCache = {};

    function pad(n) {
        return n < 10 ? '0' + n : '' + n;
    }

    // strftime subset over a display-shifted date (UTC getters only).
    function formatDate(fmt, date) {
        var h24 = date.getUTCHours();
        var h12 = h24 % 12 === 0 ? 12 : h24 % 12;

        return fmt
            .replace(/%A/g, DAYS[date.getUTCDay()])
            .replace(/%a/g, DAYS[date.getUTCDay()].substr(0, 3))
            .replace(/%B/g, MONTHS[date.getUTCMonth()])
            .replace(/%b/g, MONTHS[date.getUTCMonth()])
            .replace(/%d/g, pad(date.getUTCDate()))
            .replace(/%e/g, '' + date.getUTCDate())
            .replace(/%H/g, pad(h24))
            .replace(/%I/g, pad(h12))
            .replace(/%l/g, '' + h12)
            .replace(/%M/g, pad(date.getUTCMinutes()))
            .replace(/%S/g, pad(date.getUTCSeconds()))
            .replace(/%p/g, h24 < 12 ? 'AM' : 'PM')
            .replace(/%Y/g, '' + date.getUTCFullYear());
    }

    function remToPx(value) {
        var parsed = /^([\d.]+)rem$/.exec(value);

        return parsed ? parseFloat(parsed[1]) * 16 + 'px' : value;
    }

    function styleToFont(style, defaults) {
        style = style || {};
        defaults = defaults || {};

        var weight = style.fontWeight || defaults.fontWeight || 'normal';
        var size = remToPx(style.fontSize || defaults.fontSize || '11px');
        var family = style.fontFamily || defaults.fontFamily || '"Source Sans 3", system-ui, sans-serif';

        return weight + ' ' + size + ' ' + family;
    }

    function stripTags(html) {
        return String(html).replace(/<[^>]*>/g, '');
    }

    // Label format templates: '{value}', '{value:.2f}', or '{value:%l %p}'
    // (datetime handled by the caller); numeric precision handled here.
    function formatValue(format, value) {
        return format.replace(/\{value(?::\.(\d)f)?\}/, function (match, decimals) {
            return decimals != null ? Number(value).toFixed(decimals) : String(value);
        });
    }

    function loadImage(url, onLoad) {
        var entry = imageCache[url];

        if (!entry) {
            entry = imageCache[url] = { img: new Image(), loaded: false, callbacks: [] };
            entry.img.onload = function () {
                entry.loaded = true;
                entry.callbacks.forEach(function (cb) { cb(); });
                entry.callbacks = [];
            };
            entry.img.src = url;
        }

        if (!entry.loaded && onLoad) entry.callbacks.push(onLoad);

        return entry;
    }

    // Trace a polyline or a Catmull-Rom smoothed spline through pixel coords.
    function traceCoords(ctx, coords, smooth) {
        ctx.moveTo(coords[0][0], coords[0][1]);

        if (!smooth || coords.length < 3) {
            for (var j = 1; j < coords.length; j++) ctx.lineTo(coords[j][0], coords[j][1]);

            return;
        }

        for (var i = 1; i < coords.length; i++) {
            var p0 = coords[i - 2] || coords[i - 1];
            var p1 = coords[i - 1];
            var p2 = coords[i];
            var p3 = coords[i + 1] || p2;

            ctx.bezierCurveTo(
                p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6,
                p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6,
                p2[0], p2[1]
            );
        }
    }

    /* ----------------------------- Point / Series ----------------------------- */

    function Point(series, x, y, high) {
        this.series = series;
        this.x = x;
        this.y = y;
        this.high = high;
        this.marker = null;
    }

    Point.prototype.update = function (options) {
        if (options && options.marker) {
            this.marker = Object.assign({}, this.marker, options.marker);
        }

        this.series.chart._schedulePaint();
    };

    Object.defineProperty(Point.prototype, 'index', {
        get: function () {
            return this.series.points.indexOf(this);
        },
    });

    function Series(chart, options) {
        this.chart = chart;
        this.options = options || {};
        this.type = this.options.type || (chart.options.chart && chart.options.chart.type) || 'line';
        this.color = this.options.color;
        this.visible = true;
        this.points = [];
        this.xData = [];

        var data = this.options.data || [];

        for (var i = 0; i < data.length; i++) {
            var row = data[i];
            var point;

            if (row == null) continue;

            if (this.type === 'arearange') {
                point = new Point(this, row[0], row[2], row[2]);
                point.low = row[1];
            } else if (typeof row === 'number') {
                // Numeric rows index from pointStart by pointInterval (polar roses).
                var pointStart = this.options.pointStart != null ? this.options.pointStart : (chart.plotOptions.pointStart || 0);
                var pointInterval = this.options.pointInterval != null ? this.options.pointInterval : (chart.plotOptions.pointInterval || 1);

                point = new Point(this, pointStart + i * pointInterval, row);
            } else if (Array.isArray(row)) {
                point = new Point(this, row[0], row[1]);
            } else {
                // Object rows ({y, color, ...}) index along the category axis.
                point = new Point(this, row.x != null ? row.x : i, row.y);
                point.color = row.color;
                point.optionsRow = row;
            }

            this.points.push(point);
            this.xData.push(point.x);
        }

        // Alias used by Graph.js (xAxis[0].series[0].data).
        this.data = this.points;
    }

    // Append a point (Highcharts-style: no re-sort, so existing point indices
    // stay stable for consumers holding onto them; the painter sorts a copy).
    Series.prototype.addPoint = function (row, redraw) {
        var point;

        if (Array.isArray(row)) {
            point = new Point(this, row[0], row[1]);
        } else if (typeof row === 'number') {
            point = new Point(this, this.points.length, row);
        } else {
            point = new Point(this, row.x, row.y);
        }

        this.points.push(point);
        this.xData.push(point.x);
        this._unsorted = true;

        if (redraw !== false) this.chart.redraw();

        return point;
    };

    Object.defineProperty(Series.prototype, 'yAxis', {
        get: function () {
            return this.chart.yAxis[0];
        },
    });

    // Merge option changes (opacity, zIndex, color, ...) into the series.
    Series.prototype.update = function (options, redraw) {
        Object.assign(this.options, options);

        if (options && options.color) this.color = options.color;

        if (redraw !== false) this.chart.redraw();
    };

    Object.defineProperty(Series.prototype, 'index', {
        get: function () {
            return this.chart.series.indexOf(this);
        },
    });

    // redraw === false defers rendering until the next redraw()/paint, matching
    // Highcharts — Graph.js batches series changes and applies axis extremes and
    // chart width before the first visible render.
    Series.prototype.remove = function (redraw) {
        var index = this.chart.series.indexOf(this);

        if (index > -1) this.chart.series.splice(index, 1);

        if (redraw !== false) this.chart.redraw();
    };

    /* --------------------------------- Axes --------------------------------- */

    function XAxis(chart, options, linkedTo) {
        this.chart = chart;
        this.options = options || {};
        this.isLinked = linkedTo != null;
        this.userMin = null;
        this.userMax = null;
        this.min = null;
        this.max = null;
        this.ticks = {};
        this.minorTicks = {};
        this.tickPositions = [];
        this.plotLines = (this.options.plotLines || []).slice();
    }

    XAxis.prototype.toPixels = function (value) {
        var chart = this.chart;
        var axis = chart.xAxis[0];
        var range = axis.max - axis.min || 1;

        return chart.plotLeft + ((+value) - axis.min) / range * chart.plotWidth;
    };

    XAxis.prototype.toValue = function (pixels) {
        var chart = this.chart;
        var axis = chart.xAxis[0];
        var range = axis.max - axis.min || 1;

        return axis.min + (pixels - chart.plotLeft) / chart.plotWidth * range;
    };

    XAxis.prototype.setExtremes = function (min, max) {
        var axis = this.chart.xAxis[0];

        axis.userMin = min;
        axis.userMax = max;

        this.chart.redraw();
    };

    Object.defineProperty(XAxis.prototype, 'series', {
        get: function () {
            return this.chart.series;
        },
    });

    function YAxis(chart, options) {
        this.chart = chart;
        this.options = options || {};
        this.userMax = this.options.max != null ? this.options.max : null;
        this.min = this.options.min || 0;
        this.max = null;
        this.tickPositions = [];
        this.plotLines = (this.options.plotLines || []).slice();
    }

    YAxis.prototype.toPixels = function (value) {
        var chart = this.chart;
        var range = this.max - this.min || 1;

        return chart.plotTop + chart.plotHeight - ((+value) - this.min) / range * chart.plotHeight;
    };

    YAxis.prototype.addPlotLine = function (options) {
        this.plotLines.push(options);
        this.chart._schedulePaint();
    };

    Object.defineProperty(YAxis.prototype, 'top', {
        get: function () {
            return this.chart.plotTop;
        },
    });

    Object.defineProperty(YAxis.prototype, 'height', {
        get: function () {
            return this.chart.plotHeight;
        },
    });

    YAxis.prototype.getExtremes = function () {
        var dataMin = Infinity;
        var dataMax = -Infinity;

        this.chart.series.forEach(function (series) {
            series.points.forEach(function (point) {
                if (point.y == null) return;

                var high = point.high != null ? point.high : point.y;
                var low = point.low != null ? point.low : point.y;

                if (high > dataMax) dataMax = high;
                if (low < dataMin) dataMin = low;
            });
        });

        return {
            dataMin: isFinite(dataMin) ? dataMin : null,
            dataMax: isFinite(dataMax) ? dataMax : null,
            min: this.min,
            max: this.max,
        };
    };

    /* ------------------------------- Renderer ------------------------------- */

    function RendererElement(chart, type) {
        var self = this;

        this.chart = chart;
        this.type = type;
        this.attrs = {};
        this.element = {
            remove: function () {
                var index = chart._shapes.indexOf(self);

                if (index > -1) chart._shapes.splice(index, 1);

                chart._schedulePaint();
            },
        };
    }

    RendererElement.prototype.attr = function (attrs) {
        Object.assign(this.attrs, attrs);
        this.chart._schedulePaint();

        return this;
    };

    RendererElement.prototype.add = function () {
        this.chart._shapes.push(this);
        this.chart._schedulePaint();

        return this;
    };

    // Highcharts SVGElement parity: callers dispose shapes via destroy() or
    // remove() as well as element.remove().
    RendererElement.prototype.remove = function () {
        this.element.remove();

        return this;
    };

    RendererElement.prototype.destroy = function () {
        this.element.remove();
    };

    function Renderer(chart) {
        this.chart = chart;
    }

    Renderer.prototype.path = function (segments) {
        var el = new RendererElement(this.chart, 'path');

        if (segments) el.attrs.d = segments;

        return el;
    };

    Renderer.prototype.rect = function (x, y, width, height, r, strokeWidth) {
        var el = new RendererElement(this.chart, 'rect');

        Object.assign(el.attrs, { x: x, y: y, width: width, height: height, r: r || 0, 'stroke-width': strokeWidth || 0 });

        return el;
    };

    Renderer.prototype.image = function (href, x, y, width, height) {
        var el = new RendererElement(this.chart, 'image');
        var chart = this.chart;

        Object.assign(el.attrs, { href: href, x: x, y: y, width: width, height: height });

        loadImage(href, function () {
            chart._schedulePaint();
        });

        return el;
    };

    // DOM label (useHTML) — Graph.js only uses labels for HTML content.
    Renderer.prototype.label = function (html, x, y) {
        var el = new RendererElement(this.chart, 'label');
        var div = document.createElement('div');

        div.style.position = 'absolute';
        div.style.left = x + 'px';
        div.style.top = y + 'px';
        div.innerHTML = html;

        var originalAttr = el.attr.bind(el);

        el.attr = function (attrs) {
            if (attrs && attrs.zIndex != null) div.style.zIndex = attrs.zIndex;

            return originalAttr(attrs);
        };

        el.add = function () {
            el.chart.wrapper.appendChild(div);

            return el;
        };

        el.element = div;

        return el;
    };

    /* --------------------------------- Chart -------------------------------- */

    function CanvasChart(container, config) {
        var self = this;

        this.container = typeof container === 'string' ? document.querySelector(container) : container;
        this.options = config || {};

        var chartOptions = this.options.chart || {};
        var xAxisOptions = [].concat(this.options.xAxis || {});
        var yAxisOptions = [].concat(this.options.yAxis || {});
        var timeOptions = this.options.time || {};

        this.timezoneOffset = timeOptions.timezoneOffset || 0; // minutes, Highcharts sign (west positive)
        // Optional per-timestamp offset callback (DST-aware, like Highcharts
        // time.getTimezoneOffset). Falls back to the fixed offset.
        this._tzFn = typeof timeOptions.getTimezoneOffset === 'function' ? timeOptions.getTimezoneOffset : null;
        this.plotOptions = (this.options.plotOptions && this.options.plotOptions.series) || {};

        this.series = [];
        this._shapes = [];
        this.xAxisOffset = 0;
        this.explicitWidth = chartOptions.width || null;
        this._paintScheduled = false;
        this._inRedraw = false;
        this._yAxisExternalCanvas = null;

        // DOM: wrapper carries highcharts-container for selector compatibility.
        this.wrapper = document.createElement('div');
        this.wrapper.className = 'highcharts-container jw-canvas-chart';
        this.wrapper.style.position = 'relative';
        this.wrapper.style.width = '100%';
        this.wrapper.style.height = '100%';
        // Measure before inserting the canvas (a fresh canvas is 300x150 and
        // would inflate a content-sized container). Like Highcharts, an unsized
        // container falls back to a 400px chart.
        var containerStyle = window.getComputedStyle(this.container);
        var contentHeight = this.container.clientHeight
            - (parseFloat(containerStyle.paddingTop) || 0)
            - (parseFloat(containerStyle.paddingBottom) || 0);

        var configHeight = typeof chartOptions.height === 'string' ? parseFloat(chartOptions.height) : chartOptions.height;

        this.explicitHeight = configHeight || (contentHeight >= 40 ? contentHeight : 400);
        this.isPolar = !!chartOptions.polar;

        // Highcharts empties the render target before placing a chart —
        // consumers rely on it (e.g. spot-list rows park a loader spinner in
        // the container, and re-renders must not stack canvases).
        this.container.innerHTML = '';

        this.canvas = document.createElement('canvas');
        this.canvas.style.display = 'block';
        this.wrapper.appendChild(this.canvas);
        this.container.appendChild(this.wrapper);
        this.ctx = this.canvas.getContext('2d');

        this.renderer = new Renderer(this);

        this.xAxis = [new XAxis(this, xAxisOptions[0])];

        if (xAxisOptions.length > 1) {
            this.xAxis.push(new XAxis(this, xAxisOptions[1], 0));
        }

        this.yAxis = [new YAxis(this, yAxisOptions[0])];

        (this.options.series || []).forEach(function (seriesOptions) {
            if (seriesOptions && (seriesOptions.data || seriesOptions.id || seriesOptions.name)) {
                self.series.push(new Series(self, seriesOptions));
            }
        });

        this.events = chartOptions.events || {};

        if (this.events.click) {
            this.wrapper.addEventListener('click', function (e) {
                self.events.click.call(self, e);
            });
        }

        // Pointer layer: only when the config actually asks for interactivity
        // (Graph.js charts run with enableMouseTracking: false and skip this).
        var tooltipOptions = this.options.tooltip || {};
        var seriesEvents = this.plotOptions.events || {};
        var pointEvents = (this.plotOptions.point && this.plotOptions.point.events) || {};
        var wantsTooltip = tooltipOptions.enabled !== false && typeof tooltipOptions.formatter === 'function';
        var wantsSeriesEvents = !!(seriesEvents.mouseOver || seriesEvents.mouseOut || seriesEvents.click);
        var wantsPointEvents = !!(pointEvents.mouseOver || pointEvents.mouseOut);

        if ((wantsTooltip || wantsSeriesEvents || wantsPointEvents) && this.plotOptions.enableMouseTracking !== false) {
            this._initPointer(tooltipOptions, seriesEvents, pointEvents);
        }

        this._layout();

        if (this.events.load) this.events.load.call(this, {});

        this._paint();

        if (this.events.render) this.events.render.call(this);
    }

    var proto = CanvasChart.prototype;

    // See Series.remove: redraw === false defers rendering until the caller's
    // own redraw, so half-configured intermediate states never flash.
    proto.addSeries = function (options, redraw) {
        var series = new Series(this, options);

        this.series.push(series);

        if (redraw !== false) this.redraw();

        return series;
    };

    Object.defineProperty(CanvasChart.prototype, 'hoverPoint', {
        get: function () {
            return this._hoverPoint || null;
        },
    });

    proto.get = function (id) {
        for (var i = 0; i < this.series.length; i++) {
            if (this.series[i].options.id === id) return this.series[i];
        }

        return undefined;
    };

    proto.update = function (options, redraw) {
        options = options || {};

        if (options.chart && options.chart.width != null) {
            this.explicitWidth = options.chart.width;
        }

        if (options.yAxis) {
            if (options.yAxis.max !== undefined) this.yAxis[0].userMax = options.yAxis.max;

            Object.assign(this.yAxis[0].options, options.yAxis);
        }

        if (options.xAxis && options.xAxis.offset !== undefined) {
            this.xAxisOffset = options.xAxis.offset || 0;
        }

        if (options.series) {
            var self = this;

            this.series = options.series.map(function (seriesOptions) {
                return new Series(self, seriesOptions);
            });
        }

        if (redraw !== false) this.redraw();
    };

    proto.redraw = function () {
        this._inRedraw = true;
        this._layout();

        if (this.events.redraw) this.events.redraw.call(this);

        this._inRedraw = false;
        this._paint();

        if (this.events.render) this.events.render.call(this);
    };

    proto.destroy = function () {
        if (this.wrapper.parentElement) this.wrapper.parentElement.removeChild(this.wrapper);

        this.series = [];
        this._shapes = [];
    };

    // Graph.js hook: render the y axis into the fixed side container instead of
    // the scrolled chart canvas (replaces the old SVG-node reparenting).
    proto.renderYAxisExternal = function (container) {
        if (!this._yAxisExternalCanvas) {
            var canvas = container.querySelector('canvas.jw-graph-yAxis-labels');

            if (!canvas) {
                canvas = document.createElement('canvas');
                canvas.className = 'jw-graph-yAxis-labels';
                container.appendChild(canvas);
            }

            this._yAxisExternalCanvas = canvas;
        }

        this._paintExternalYAxis();
    };

    /* ------------------------------ Pointer ------------------------------ */

    proto._initPointer = function (tooltipOptions, seriesEvents, pointEvents) {
        var self = this;

        this._tooltipOptions = tooltipOptions;
        this._seriesEvents = seriesEvents;
        this._pointEvents = pointEvents || {};
        this._hoverSeries = null;
        this._hoverPoint = null;
        this._tooltipEl = null;

        this.wrapper.addEventListener('pointermove', function (e) {
            var rect = self.canvas.getBoundingClientRect();
            var x = e.clientX - rect.left;
            var y = e.clientY - rect.top;

            self._applyHover(self._hitTest(x, y), x, y);
        });

        this.wrapper.addEventListener('pointerleave', function () {
            self._applyHover(null);
        });

        this.wrapper.addEventListener('click', function (e) {
            if (self._hoverSeries && seriesEvents.click) seriesEvents.click.call(self._hoverSeries, e);
        });
    };

    // Nearest point across all series, measured to the point's high and low
    // edges (arearange bands hover by whichever edge is closer).
    proto._hitTest = function (px, py) {
        if (px < this.plotLeft || px > this.plotLeft + this.plotWidth || py < this.plotTop || py > this.plotTop + this.plotHeight) {
            return null;
        }

        var xAxis = this.xAxis[0];
        var yAxis = this.yAxis[0];
        var xValue = xAxis.toValue(px);
        var best = null;

        for (var s = 0; s < this.series.length; s++) {
            var series = this.series[s];

            if (!series.visible || series.points.length === 0) continue;
            if (series.options.enableMouseTracking === false) continue;

            var points = series.points;
            var lo = 0;
            var hi = points.length - 1;

            while (hi - lo > 1) {
                var mid = (lo + hi) >> 1;

                if (points[mid].x < xValue) lo = mid;
                else hi = mid;
            }

            var isRange = series.type === 'arearange';

            for (var k = lo; k <= hi; k++) {
                var point = points[k];

                if (point.y == null) continue;

                var dx = xAxis.toPixels(point.x) - px;
                var dist;

                if (isRange) {
                    // Bands hover by whichever edge is closer.
                    var dyHigh = yAxis.toPixels(point.high != null ? point.high : point.y) - py;

                    dist = dx * dx + dyHigh * dyHigh;

                    if (point.low != null) {
                        var dyLow = yAxis.toPixels(point.low) - py;

                        dist = Math.min(dist, dx * dx + dyLow * dyLow);
                    }
                } else {
                    // Line-family series hover by x distance (Highcharts
                    // findNearestPointBy 'x'), so the tooltip works anywhere
                    // in the plot column.
                    dist = dx * dx;
                }

                if (!best || dist < best.dist) {
                    best = { series: series, point: point, dist: dist, isRange: isRange };
                }
            }
        }

        // Range bands ignore hits far from an edge (stickyTracking: false).
        if (best && best.isRange && best.dist > 40 * 40) return null;

        return best;
    };

    proto._applyHover = function (hit, px, py) {
        var series = hit ? hit.series : null;
        var point = hit ? hit.point : null;
        var events = this._seriesEvents || {};
        var pointEvents = this._pointEvents || {};

        if (point !== this._hoverPoint) {
            if (this._hoverPoint && pointEvents.mouseOut) pointEvents.mouseOut.call(this._hoverPoint, { target: this._hoverPoint });

            this._hoverPoint = point;

            if (point && pointEvents.mouseOver) pointEvents.mouseOver.call(point, { target: point });
        }

        if (series !== this._hoverSeries) {
            if (this._hoverSeries && events.mouseOut) events.mouseOut.call(this._hoverSeries);

            this._hoverSeries = series;

            if (series && events.mouseOver) events.mouseOver.call(series);

            // Repaint for the inactive-state dimming of non-hovered series.
            this._schedulePaint();
        }

        this._updateTooltip(hit, px, py);
    };

    proto._updateTooltip = function (hit, px, py) {
        var options = this._tooltipOptions || {};

        if (options.enabled === false || typeof options.formatter !== 'function') return;

        if (!hit) {
            if (this._tooltipEl) this._tooltipEl.style.display = 'none';

            return;
        }

        if (!this._tooltipEl) {
            var el = document.createElement('div');

            el.className = 'jw-canvas-chart-tooltip';
            el.style.position = 'absolute';
            el.style.pointerEvents = 'none';
            el.style.zIndex = 30;
            this.wrapper.appendChild(el);
            this._tooltipEl = el;
        }

        var context = { series: hit.series, point: hit.point, x: hit.point.x, y: hit.point.y };

        if (options.shared) {
            var sharedPoints = [];

            this.series.forEach(function (series) {
                if (!series.visible || series.options.enableMouseTracking === false || series.points.length === 0) return;

                var pts = series.points;
                var lo = 0;
                var hi = pts.length - 1;

                while (hi - lo > 1) {
                    var mid = (lo + hi) >> 1;

                    if (pts[mid].x < hit.point.x) lo = mid;
                    else hi = mid;
                }

                var nearest = Math.abs(pts[lo].x - hit.point.x) <= Math.abs(pts[hi].x - hit.point.x) ? pts[lo] : pts[hi];

                if (nearest.y != null) sharedPoints.push(nearest);
            });

            context.points = sharedPoints;
        }

        var html = options.formatter.call(context);

        if (html === false) {
            this._tooltipEl.style.display = 'none';

            return;
        }

        var anchorX;
        var anchorY;

        if (options.followPointer) {
            anchorX = px + 10;
            anchorY = py - 22;
        } else {
            anchorX = this.xAxis[0].toPixels(hit.point.x) + 10;
            anchorY = this.yAxis[0].toPixels(hit.point.high != null ? hit.point.high : hit.point.y) - 22;
        }

        this._tooltipEl.innerHTML = html || '';
        this._tooltipEl.style.left = anchorX + 'px';
        this._tooltipEl.style.top = anchorY + 'px';
        this._tooltipEl.style.display = 'block';
    };

    proto._schedulePaint = function () {
        var self = this;

        if (this._paintScheduled || this._inRedraw) return;

        this._paintScheduled = true;

        window.requestAnimationFrame(function () {
            self._paintScheduled = false;
            self._paint();
        });
    };

    /* ------------------------------ Layout ------------------------------ */

    proto._spacing = function () {
        var chartOptions = this.options.chart || {};
        var spacing = (chartOptions.spacing || [10, 10, 15, 10]).slice();
        var margin = chartOptions.margin;

        if (chartOptions.spacingTop != null) spacing[0] = chartOptions.spacingTop;
        if (chartOptions.spacingRight != null) spacing[1] = chartOptions.spacingRight;
        if (chartOptions.spacingBottom != null) spacing[2] = chartOptions.spacingBottom;
        if (chartOptions.spacingLeft != null) spacing[3] = chartOptions.spacingLeft;

        return {
            top: chartOptions.marginTop != null ? chartOptions.marginTop : (margin != null ? margin[0] : spacing[0]),
            right: chartOptions.marginRight != null ? chartOptions.marginRight : (margin != null ? margin[1] : spacing[1]),
            bottom: chartOptions.marginBottom != null ? chartOptions.marginBottom : (margin != null ? margin[2] : spacing[2]),
            left: chartOptions.marginLeft != null ? chartOptions.marginLeft : (margin != null ? margin[3] : spacing[3]),
            hasMarginTop: chartOptions.marginTop != null || margin != null,
            hasMarginBottom: chartOptions.marginBottom != null || margin != null,
            hasMarginLeft: chartOptions.marginLeft != null || margin != null,
        };
    };

    proto._xLabelOptions = function (axisIndex) {
        var axis = this.xAxis[axisIndex] || this.xAxis[0];

        return (axis.options && axis.options.labels) || {};
    };

    proto._layout = function () {
        var width = this.explicitWidth || this.container.clientWidth || 300;
        var height = this.explicitHeight;

        this.chartWidth = width;
        this.chartHeight = height;

        var dpr = window.devicePixelRatio || 1;

        if (width * height * dpr * dpr > MAX_CANVAS_AREA) {
            dpr = Math.sqrt(MAX_CANVAS_AREA / (width * height));
        }

        // Browsers also cap a single canvas dimension (~32k px).
        if (width * dpr > 32000) dpr = 32000 / width;
        if (height * dpr > 32000) dpr = Math.min(dpr, 32000 / height);

        this.dpr = dpr;

        if (this.canvas.width !== Math.round(width * dpr) || this.canvas.height !== Math.round(height * dpr)) {
            this.canvas.width = Math.round(width * dpr);
            this.canvas.height = Math.round(height * dpr);
            this.canvas.style.width = width + 'px';
            this.canvas.style.height = height + 'px';
        }

        var spacing = this._spacing();
        var xAxis0 = this.xAxis[0];
        var yAxis = this.yAxis[0];
        var xLabels = this._xLabelOptions(0);
        var yLabels = (yAxis.options && yAxis.options.labels) || {};
        var xLabelsEnabled = xLabels.enabled !== false;
        var yLabelsEnabled = yLabels.enabled !== false;
        var yAxisExternal = this._yAxisExternalCanvas !== null;

        // X extremes.
        var dataMinX = Infinity;
        var dataMaxX = -Infinity;

        this.series.forEach(function (series) {
            series.points.forEach(function (point) {
                if (point.x < dataMinX) dataMinX = point.x;
                if (point.x > dataMaxX) dataMaxX = point.x;
            });
        });

        if (!isFinite(dataMinX)) {
            dataMinX = 0;
            dataMaxX = 1;
        }

        var optMin = xAxis0.options.min;
        var minPadding = xAxis0.options.minPadding || 0;
        var maxPadding = xAxis0.options.maxPadding || 0;
        var rawRange = dataMaxX - dataMinX || 1;

        var min = xAxis0.userMin != null ? xAxis0.userMin : (optMin != null ? optMin : dataMinX - rawRange * minPadding);
        var max = xAxis0.userMax != null ? xAxis0.userMax : dataMaxX + rawRange * maxPadding;

        // Category axes span the index bands, half a band beyond each end.
        if (xAxis0.options.categories) {
            min = -0.5;
            max = xAxis0.options.categories.length - 0.5;
        }

        // Polar axes wrap; use the exact data extremes (0..360 for a rose).
        if (this.isPolar) {
            min = dataMinX;
            max = dataMaxX;
        }

        if (max <= min) max = min + 1;

        xAxis0.min = min;
        xAxis0.max = max;

        // Y extremes + ticks.
        var dataMaxY = -Infinity;

        this.series.forEach(function (series) {
            series.points.forEach(function (point) {
                if (point.y != null && point.y > dataMaxY) dataMaxY = point.y;
            });
        });

        if (!isFinite(dataMaxY)) dataMaxY = 1;

        var explicitYMax = yAxis.userMax != null ? yAxis.userMax : yAxis.options.max;
        var yMin = yAxis.options.min != null ? yAxis.options.min : 0;
        var yMaxPadding = yAxis.options.maxPadding != null ? yAxis.options.maxPadding : 0.05;
        var yMax = explicitYMax != null ? explicitYMax : dataMaxY + Math.max(dataMaxY - yMin, 0.1) * yMaxPadding;

        yAxis.min = yMin;

        var explicitYInterval = yAxis.options.tickInterval;
        var endOnTick = yAxis.options.endOnTick !== false && !explicitYInterval;
        var allowDecimals = yAxis.options.allowDecimals !== false;
        var yTickPixelInterval = yAxis.options.tickPixelInterval || 72;
        var yMinTickInterval = yAxis.options.minTickInterval || 0;

        // Provisional plot height for tick counting (bottom space estimated below).
        var xLabelSpace = 0;

        if (xLabelsEnabled && !this.xAxis[1]) {
            xLabelSpace = Math.max(xAxis0.options.tickLength || 0, (xLabels.y || 20) + 4);
        }

        var plotTop = spacing.hasMarginTop ? spacing.top : spacing.top;
        var plotBottom = height - spacing.bottom - xLabelSpace - this.xAxisOffset;
        var plotHeightEstimate = Math.max(10, plotBottom - plotTop);

        if (yAxis.options.tickPositions) {
            // Explicit tick positions define the axis extremes outright.
            yAxis.tickPositions = yAxis.options.tickPositions.slice();
            yMin = yAxis.tickPositions[0];
            yMax = yAxis.tickPositions[yAxis.tickPositions.length - 1];
            yAxis.min = yMin;
            yAxis.max = yMax;
        } else {
            var yTickInterval;

            if (explicitYInterval) {
                yTickInterval = explicitYInterval;
            } else {
                var yRange = Math.max(yMax - yMin, 1);
                var targetTicks = Math.max(2, plotHeightEstimate / yTickPixelInterval);
                var roughInterval = yRange / targetTicks;

                yTickInterval = niceInterval(roughInterval, allowDecimals);

                if (yMinTickInterval && yTickInterval < yMinTickInterval) yTickInterval = yMinTickInterval;
            }

            if (endOnTick) {
                yMax = Math.ceil(yMax / yTickInterval) * yTickInterval;
            }

            yAxis.max = yMax;
            yAxis.tickInterval = yTickInterval;
            yAxis.tickPositions = [];

            for (var tick = yMin; tick <= yMax + 1e-9; tick += yTickInterval) {
                yAxis.tickPositions.push(Math.round(tick * 1000) / 1000);
            }
        }

        // Y label space (only when the axis renders inside the chart).
        var yLabelSpace = 0;

        if (yLabelsEnabled && !yAxisExternal && !spacing.hasMarginLeft) {
            var ctx = this.ctx;

            ctx.font = styleToFont(yLabels.style, { fontSize: '11px' });

            var maxLabelWidth = 0;

            var chartSelf = this;

            yAxis.tickPositions.forEach(function (position) {
                maxLabelWidth = Math.max(maxLabelWidth, ctx.measureText(chartSelf._yLabelText(yLabels, position)).width);
            });

            yLabelSpace = maxLabelWidth + 8;

            var yTitle = (yAxis.options && yAxis.options.title) || {};

            if (yTitle.text && yTitle.enabled !== false && !yTitle.floating) {
                yLabelSpace += 16;
            }
        }

        this._yLabelSpace = yLabelSpace;

        this.plotTop = plotTop;
        this.plotLeft = spacing.hasMarginLeft ? spacing.left : spacing.left + yLabelSpace;
        this.plotWidth = Math.max(10, width - this.plotLeft - spacing.right);
        this.plotHeight = Math.max(10, plotBottom - this.plotTop);

        if (this.isPolar) {
            this.polarCenter = {
                x: this.plotLeft + this.plotWidth / 2,
                y: this.plotTop + this.plotHeight / 2,
            };
            // Highcharts default pane size is 85% of the smaller plot dimension,
            // leaving room for the compass labels around the rim.
            this.polarRadius = Math.min(this.plotWidth, this.plotHeight) * 0.425;
        }

        // Highcharts plotX/plotY contract: plot-relative pixel positions,
        // consumed by the JWX tide marker components.
        if (!this.isPolar) {
            for (var si = 0; si < this.series.length; si++) {
                var seriesPoints = this.series[si].points;

                for (var pi = 0; pi < seriesPoints.length; pi++) {
                    var pt = seriesPoints[pi];

                    pt.plotX = this.xAxis[0].toPixels(pt.x) - this.plotLeft;
                    pt.plotY = pt.y == null ? null : this.yAxis[0].toPixels(pt.high != null ? pt.high : pt.y) - this.plotTop;
                }
            }
        }

        this._computeXTicks();
    };

    proto._polarAngle = function (value) {
        var xAxis = this.xAxis[0];
        var pane = this.options.pane || {};
        var start = (pane.startAngle || 0) - 90; // 0deg points up
        var end = pane.endAngle != null ? pane.endAngle - 90 : start + 360;
        var range = xAxis.max - xAxis.min || 1;

        return (start + (value - xAxis.min) / range * (end - start)) * Math.PI / 180;
    };

    proto._polarPoint = function (xValue, yValue) {
        var yAxis = this.yAxis[0];
        var range = yAxis.max - yAxis.min || 1;
        var radius = Math.max(0, (yValue - yAxis.min) / range) * this.polarRadius;
        var angle = this._polarAngle(xValue);

        return {
            x: this.polarCenter.x + Math.cos(angle) * radius,
            y: this.polarCenter.y + Math.sin(angle) * radius,
        };
    };

    function niceInterval(rough, allowDecimals) {
        var magnitude = Math.pow(10, Math.floor(Math.log(rough) / Math.LN10));
        var candidates = [1, 2, 5, 10];

        for (var i = 0; i < candidates.length; i++) {
            var interval = candidates[i] * magnitude;

            if (!allowDecimals && interval < 1) continue;

            if (interval >= rough) return interval;
        }

        return 10 * magnitude;
    }

    proto._tzMsAt = function (utcMs) {
        return (this._tzFn ? this._tzFn(utcMs) : this.timezoneOffset) * 60000;
    };

    proto._computeXTicks = function () {
        var xAxis0 = this.xAxis[0];
        var labelAxis = this.xAxis[1] || xAxis0;
        var tzMs = this.timezoneOffset * 60000;

        // Category axes: one tick per category; no datetime label machinery.
        if (xAxis0.options.categories) {
            var categories = xAxis0.options.categories;
            var categoryLabels = (labelAxis.options && labelAxis.options.labels) || {};

            labelAxis.ticks = {};
            labelAxis.minorTicks = {};
            labelAxis.tickPositions = [];
            xAxis0.tickPositions = [];

            labelAxis.labels = categories.map(function (category, i) {
                return {
                    value: i,
                    text: String(category),
                    bold: false,
                    skip: categoryLabels.enabled === false,
                };
            });

            return;
        }
        var min = xAxis0.min;
        var max = xAxis0.max;
        var range = max - min || 1;
        var pxPerMs = this.plotWidth / range;
        var tickPixelInterval = labelAxis.options.tickPixelInterval || xAxis0.options.tickPixelInterval || 100;
        var minTickInterval = xAxis0.options.minTickInterval || 0;

        var candidates = [
            15 * 60000, 30 * 60000,
            HOUR, 2 * HOUR, 3 * HOUR, 4 * HOUR, 6 * HOUR, 8 * HOUR, 12 * HOUR,
            DAY, 2 * DAY, 7 * DAY,
        ];

        var interval = candidates[candidates.length - 1];

        if (xAxis0.options.tickInterval) {
            interval = xAxis0.options.tickInterval;
        } else {
            for (var i = 0; i < candidates.length; i++) {
                if (candidates[i] * pxPerMs >= tickPixelInterval && candidates[i] >= minTickInterval) {
                    interval = candidates[i];
                    break;
                }
            }
        }

        xAxis0.tickInterval = interval;

        // Ticks aligned in display space so day boundaries land on local midnight.
        var displayMin = min - this._tzMsAt(min);
        var displayMax = max - this._tzMsAt(max);
        var ticks = {};
        var tickPositions = [];

        for (var displayTick = Math.ceil(displayMin / interval) * interval; displayTick <= displayMax; displayTick += interval) {
            var utc = displayTick + this._tzMsAt(displayTick);

            ticks[utc] = { value: utc, display: displayTick };
            tickPositions.push(utc);
        }

        // A wrapping polar axis would put its last tick on top of its first.
        if (this.isPolar && tickPositions.length > 1 && tickPositions[tickPositions.length - 1] >= max) {
            delete ticks[tickPositions.pop()];
        }

        labelAxis.ticks = ticks;
        labelAxis.tickPositions = tickPositions;
        xAxis0.tickPositions = tickPositions;

        // Minor ticks (hourly grid for the detailed graph).
        var minorInterval = labelAxis.options.minorTickInterval;
        var minorTicks = {};

        if (minorInterval) {
            for (var minorDisplay = Math.ceil(displayMin / minorInterval) * minorInterval; minorDisplay <= displayMax; minorDisplay += minorInterval) {
                minorTicks[minorDisplay + tzMs] = true;
            }
        }

        labelAxis.minorTicks = minorTicks;

        this._buildXLabels(labelAxis, interval, tzMs);
    };

    proto._buildXLabels = function (labelAxis, interval, tzMs) {
        var chart = this;
        var labelOptions = (labelAxis.options && labelAxis.options.labels) || {};
        var formats = labelAxis.options.dateTimeLabelFormats || (this.xAxis[0].options && this.xAxis[0].options.dateTimeLabelFormats) || {};
        var dayFormat = formats.day || '%b %e';
        var hourFormat = formats.hour || '%l %p';
        var minuteFormat = formats.minute || '%l:%M %p';

        function defaultLabelFormatter() {
            var displayDate = new Date(this.value - chart._tzMsAt(this.value));
            var format;

            if (interval >= DAY || (this.value - chart._tzMsAt(this.value)) % DAY === 0) {
                format = dayFormat;
            } else if (interval >= HOUR) {
                format = hourFormat;
            } else {
                format = minuteFormat;
            }

            return formatDate(format, displayDate);
        }

        labelAxis.labels = labelAxis.tickPositions.map(function (value) {
            var context = { value: value, axis: { defaultLabelFormatter: defaultLabelFormatter } };
            var text;

            if (typeof labelOptions.formatter === 'function') {
                text = labelOptions.formatter.call(context);
            } else if (labelOptions.format) {
                text = labelOptions.format.replace(/\{value:([^}]+)\}/, function (match, fmt) {
                    return formatDate(fmt, new Date(value - chart._tzMsAt(value)));
                });
            } else {
                text = defaultLabelFormatter.call(context);
            }

            var isBold = /font-weight:\s*bold/.test(text);

            return {
                value: value,
                text: stripTags(text),
                bold: isBold,
                skip: (labelOptions.enabled === false) || !text,
            };
        });
    };

    /* ------------------------------ Painting ------------------------------ */

    proto._paint = function () {
        var ctx = this.ctx;
        var chartOptions = this.options.chart || {};

        ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        ctx.clearRect(0, 0, this.chartWidth, this.chartHeight);

        if (chartOptions.backgroundColor && chartOptions.backgroundColor !== 'transparent') {
            ctx.fillStyle = chartOptions.backgroundColor;
            ctx.fillRect(0, 0, this.chartWidth, this.chartHeight);
        }

        if (chartOptions.plotBackgroundColor) {
            ctx.fillStyle = chartOptions.plotBackgroundColor;
            ctx.fillRect(this.plotLeft, this.plotTop, this.plotWidth, this.plotHeight);
        }

        if (this.isPolar) {
            this._paintPolarAxes(ctx);
        } else {
            this._paintYGridLines(ctx);
            this._paintXGridLines(ctx);
        }

        // Series and renderer shapes share one z-sorted display list, matching
        // how Highcharts interleaved them.
        var items = [];

        this.series.forEach(function (series) {
            items.push({ z: series.options.zIndex || 0, series: series });
        });

        this._shapes.forEach(function (shape) {
            items.push({ z: shape.attrs.zIndex || 0, shape: shape });
        });

        items.sort(function (a, b) {
            return a.z - b.z;
        });

        var self = this;

        items.forEach(function (item) {
            if (item.series) {
                self._paintSeries(ctx, item.series);
            } else {
                self._paintShape(ctx, item.shape);
            }
        });

        if (this.isPolar) {
            this._paintPolarXLabels(ctx);

            return;
        }

        this._paintPlotLines(ctx);
        this._paintXAxis(ctx);

        if (this._yAxisExternalCanvas) {
            this._paintExternalYAxis();
        } else {
            this._paintYAxisLabels(ctx);
            this._paintYAxisTitle(ctx);
        }
    };

    proto._paintPolarAxes = function (ctx) {
        var xAxis = this.xAxis[0];
        var yAxis = this.yAxis[0];
        var center = this.polarCenter;
        var radius = this.polarRadius;

        // Outer circle (the polar axis line).
        ctx.strokeStyle = xAxis.options.lineColor || '#e6e6e6';
        ctx.lineWidth = xAxis.options.lineWidth != null ? xAxis.options.lineWidth : 1;

        if (ctx.lineWidth) {
            ctx.beginPath();
            ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
            ctx.stroke();
        }

        // Angular spokes (xAxis grid; Highcharts defaults these off).
        var spokeWidth = xAxis.options.gridLineWidth || 0;

        if (spokeWidth) {
            ctx.strokeStyle = xAxis.options.gridLineColor || '#e6e6e6';
            ctx.lineWidth = spokeWidth;
            ctx.beginPath();

            for (var t = 0; t < xAxis.tickPositions.length; t++) {
                var angle = this._polarAngle(xAxis.tickPositions[t]);

                ctx.moveTo(center.x, center.y);
                ctx.lineTo(center.x + Math.cos(angle) * radius, center.y + Math.sin(angle) * radius);
            }

            ctx.stroke();
        }

        // Concentric rings (yAxis grid).
        var ringWidth = yAxis.options.gridLineWidth != null ? yAxis.options.gridLineWidth : 1;

        if (ringWidth) {
            ctx.strokeStyle = yAxis.options.gridLineColor || '#e6e6e6';
            ctx.lineWidth = ringWidth;

            for (var i = 0; i < yAxis.tickPositions.length; i++) {
                var value = yAxis.tickPositions[i];

                if (value <= yAxis.min) continue;

                var r = (value - yAxis.min) / (yAxis.max - yAxis.min || 1) * radius;

                ctx.beginPath();
                ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
                ctx.stroke();
            }
        }
    };

    proto._paintPolarXLabels = function (ctx) {
        var xAxis = this.xAxis[0];
        var labels = xAxis.labels || [];
        var labelOptions = (xAxis.options && xAxis.options.labels) || {};
        var center = this.polarCenter;
        var labelRadius = this.polarRadius + 10;

        ctx.font = styleToFont(labelOptions.style, { fontSize: '11px' });
        ctx.fillStyle = (labelOptions.style && labelOptions.style.color) || '#666666';
        ctx.textBaseline = 'middle';

        for (var i = 0; i < labels.length; i++) {
            var label = labels[i];

            if (label.skip || !label.text) continue;

            var angle = this._polarAngle(label.value);
            var cos = Math.cos(angle);
            var x = center.x + cos * labelRadius;
            var y = center.y + Math.sin(angle) * labelRadius;

            ctx.textAlign = Math.abs(cos) < 0.3 ? 'center' : (cos > 0 ? 'left' : 'right');
            ctx.fillText(label.text, x, y);
        }
    };

    proto._paintPolarSeries = function (ctx, series) {
        var seriesOptions = series.options;
        var plotSeries = this.plotOptions;
        var lineWidth = seriesOptions.lineWidth != null ? seriesOptions.lineWidth : (plotSeries.lineWidth != null ? plotSeries.lineWidth : 1);
        var opacity = seriesOptions.opacity != null ? seriesOptions.opacity : 1;
        var color = typeof series.color === 'string' ? series.color : '#333333';
        var started = false;

        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.beginPath();

        for (var i = 0; i < series.points.length; i++) {
            var point = series.points[i];

            if (point.y == null) continue;

            var pos = this._polarPoint(point.x, point.y);

            if (!started) {
                ctx.moveTo(pos.x, pos.y);
                started = true;
            } else {
                ctx.lineTo(pos.x, pos.y);
            }
        }

        if (!started) {
            ctx.restore();

            return;
        }

        ctx.closePath();

        if (series.type === 'area') {
            ctx.save();
            ctx.globalAlpha = opacity * (seriesOptions.fillOpacity != null ? seriesOptions.fillOpacity : 0.75);
            ctx.fillStyle = color;
            ctx.fill();
            ctx.restore();
        }

        if (lineWidth > 0) {
            ctx.strokeStyle = color;
            ctx.lineWidth = lineWidth;
            ctx.lineJoin = 'round';
            ctx.stroke();
        }

        ctx.restore();
    };

    proto._paintYGridLines = function (ctx) {
        var yAxis = this.yAxis[0];
        var gridLineWidth = yAxis.options.gridLineWidth != null ? yAxis.options.gridLineWidth : 1;

        if (!gridLineWidth) return;

        ctx.strokeStyle = yAxis.options.gridLineColor || '#e6e6e6';
        ctx.lineWidth = gridLineWidth;
        ctx.beginPath();

        for (var i = 0; i < yAxis.tickPositions.length; i++) {
            var y = Math.round(yAxis.toPixels(yAxis.tickPositions[i])) + 0.5;

            ctx.moveTo(this.plotLeft, y);
            ctx.lineTo(this.plotLeft + this.plotWidth, y);
        }

        ctx.stroke();
    };

    proto._paintXGridLines = function (ctx) {
        var xAxis = this.xAxis[0];
        var gridLineWidth = xAxis.options.gridLineWidth || 0;

        if (!gridLineWidth) return;

        ctx.strokeStyle = xAxis.options.gridLineColor || '#e6e6e6';
        ctx.lineWidth = gridLineWidth;
        ctx.beginPath();

        for (var i = 0; i < xAxis.tickPositions.length; i++) {
            var x = Math.round(xAxis.toPixels(xAxis.tickPositions[i])) + 0.5;

            if (x < this.plotLeft - 1 || x > this.plotLeft + this.plotWidth + 1) continue;

            ctx.moveTo(x, this.plotTop);
            ctx.lineTo(x, this.plotTop + this.plotHeight);
        }

        ctx.stroke();
    };

    proto._paintPlotLines = function (ctx) {
        var self = this;
        var yAxis = this.yAxis[0];
        var xAxis = this.xAxis[0];

        yAxis.plotLines.forEach(function (line) {
            var y = Math.round(yAxis.toPixels(line.value)) + 0.5;

            ctx.strokeStyle = line.color || '#e2e2e2';
            ctx.lineWidth = Number(line.width) || 1;
            ctx.beginPath();
            ctx.moveTo(self.plotLeft, y);
            ctx.lineTo(self.plotLeft + self.plotWidth, y);
            ctx.stroke();
        });

        xAxis.plotLines.forEach(function (line) {
            if (line.value == null) return;

            var x = Math.round(xAxis.toPixels(line.value)) + 0.5;

            ctx.strokeStyle = line.color || '#e2e2e2';
            ctx.lineWidth = Number(line.width) || 1;
            ctx.beginPath();
            ctx.moveTo(x, self.plotTop);
            ctx.lineTo(x, self.plotTop + self.plotHeight);
            ctx.stroke();
        });
    };

    proto._paintSeries = function (ctx, series) {
        if (!series.visible || series.points.length === 0) return;

        if (this.isPolar) {
            this._paintPolarSeries(ctx, series);

            return;
        }

        var chart = this;
        var xAxis = this.xAxis[0];
        var yAxis = this.yAxis[0];
        var seriesOptions = series.options;
        var plotSeries = this.plotOptions;
        var lineWidth = seriesOptions.lineWidth != null ? seriesOptions.lineWidth : (plotSeries.lineWidth != null ? plotSeries.lineWidth : 2);
        var opacity = seriesOptions.opacity != null ? seriesOptions.opacity : 1;

        // Hovering a series dims the others (Highcharts states.inactive);
        // configs opt out with states: { inactive: { opacity: 1 } }. Shared
        // tooltips hover every series at once, so nothing dims (NCWind).
        var sharedTooltip = this._tooltipOptions && this._tooltipOptions.shared;

        if (this._hoverSeries && this._hoverSeries !== series && !sharedTooltip) {
            var states = plotSeries.states || {};
            var inactiveOpacity = states.inactive && states.inactive.opacity != null ? states.inactive.opacity : 0.2;

            opacity *= inactiveOpacity;
        }

        var color = seriesOptions.color;
        var gradient = null;

        if (color && typeof color === 'object' && color.stops) {
            var first = series.points[0];
            var last = series.points[series.points.length - 1];

            gradient = ctx.createLinearGradient(xAxis.toPixels(first.x), 0, xAxis.toPixels(last.x), 0);
            color.stops.forEach(function (stop) {
                gradient.addColorStop(Math.min(1, Math.max(0, stop[0])), stop[1]);
            });
        }

        ctx.save();
        ctx.beginPath();
        ctx.rect(this.plotLeft, this.plotTop - 6, this.plotWidth, this.plotHeight + 6);
        ctx.clip();
        ctx.globalAlpha = opacity;

        if (series.type === 'column') {
            this._paintColumnSeries(ctx, series);
            ctx.restore();

            return;
        }

        // Painter reads a sorted view when addPoint appended out of order
        // (point indices stay stable for consumers, Highcharts-style).
        var sourcePoints = series.points;

        if (series._unsorted) {
            sourcePoints = sourcePoints.slice().sort(function (a, b) {
                return a.x - b.x;
            });
        }

        // Contiguous runs of non-null points (connectNulls: false).
        var runs = [];
        var run = [];

        sourcePoints.forEach(function (point) {
            if (point.y == null) {
                if (run.length) runs.push(run);
                run = [];
            } else {
                run.push(point);
            }
        });

        if (run.length) runs.push(run);

        var zeroY = yAxis.toPixels(Math.max(yAxis.min, 0));
        var smooth = series.type === 'areaspline' || series.type === 'spline';

        runs.forEach(function (runPoints) {
            var i;

            if (series.type === 'arearange') {
                ctx.beginPath();

                for (i = 0; i < runPoints.length; i++) {
                    var xHigh = xAxis.toPixels(runPoints[i].x);
                    var yHigh = yAxis.toPixels(runPoints[i].high != null ? runPoints[i].high : runPoints[i].y);

                    if (i === 0) ctx.moveTo(xHigh, yHigh);
                    else ctx.lineTo(xHigh, yHigh);
                }

                for (i = runPoints.length - 1; i >= 0; i--) {
                    ctx.lineTo(xAxis.toPixels(runPoints[i].x), yAxis.toPixels(runPoints[i].low || 0));
                }

                ctx.closePath();
                ctx.fillStyle = gradient || color || '#999999';
                ctx.fill();

                return;
            }

            var coords = [];

            for (i = 0; i < runPoints.length; i++) {
                coords.push([xAxis.toPixels(runPoints[i].x), yAxis.toPixels(runPoints[i].y)]);
            }

            if (series.type === 'area' || series.type === 'areaspline') {
                var fillStyle;
                var fillAlpha;

                if (seriesOptions.fillColor) {
                    // Explicit fillColor (possibly a gradient) is used as-is.
                    fillStyle = chart._resolveFillStyle(ctx, seriesOptions.fillColor);
                    fillAlpha = opacity;
                } else {
                    fillStyle = gradient || color || '#999999';
                    fillAlpha = opacity * (seriesOptions.fillOpacity != null ? seriesOptions.fillOpacity : 0.75);
                }

                ctx.save();
                ctx.globalAlpha = fillAlpha;
                ctx.beginPath();
                traceCoords(ctx, coords, smooth);
                ctx.lineTo(coords[coords.length - 1][0], zeroY);
                ctx.lineTo(coords[0][0], zeroY);
                ctx.closePath();
                ctx.fillStyle = fillStyle;
                ctx.fill();
                ctx.restore();
            }

            if (lineWidth > 0) {
                // x-zones stroke the same path in clipped bands per zone color.
                var zones = seriesOptions.zoneAxis === 'x' && seriesOptions.zones && seriesOptions.zones.length ? seriesOptions.zones : null;
                var segments = zones || [{ color: null }];
                var prevPx = chart.plotLeft;

                segments.forEach(function (zone) {
                    var rightPx = zone.value != null ? xAxis.toPixels(zone.value) : chart.plotLeft + chart.plotWidth;

                    if (rightPx <= prevPx) {
                        prevPx = Math.max(prevPx, rightPx);

                        return;
                    }

                    ctx.save();

                    if (zones) {
                        ctx.beginPath();
                        ctx.rect(prevPx, chart.plotTop - 8, rightPx - prevPx, chart.plotHeight + 16);
                        ctx.clip();
                    }

                    ctx.beginPath();
                    traceCoords(ctx, coords, smooth);
                    ctx.strokeStyle = zone.color || gradient || (typeof color === 'string' ? color : '#333333');
                    ctx.lineWidth = lineWidth;
                    ctx.lineJoin = 'round';
                    ctx.lineCap = 'round';
                    ctx.stroke();
                    ctx.restore();

                    prevPx = rightPx;
                });
            }
        });

        // Markers: only when a marker config exists (Highcharts hides markers
        // on dense line series by default; tide splines have no marker config).
        var baseMarker = Object.assign({}, plotSeries.marker, seriesOptions.marker);
        var baseRadius = baseMarker.radius != null ? baseMarker.radius : 4;
        var hasMarkerConfig = plotSeries.marker != null || seriesOptions.marker != null;
        var markersEnabled = hasMarkerConfig ? baseMarker.enabled !== false : false;

        if (markersEnabled) {
            series.points.forEach(function (point) {
                if (point.y == null) return;

                var radius = point.marker && point.marker.radius != null ? point.marker.radius : baseRadius;

                if (radius < 0.5) return;

                var markerX = xAxis.toPixels(point.x);
                var markerY = yAxis.toPixels(point.high != null ? point.high : point.y);

                ctx.beginPath();
                ctx.arc(markerX, markerY, radius, 0, Math.PI * 2);
                ctx.fillStyle = baseMarker.fillColor || (typeof color === 'string' ? color : '#333333');
                ctx.fill();

                if (baseMarker.lineWidth) {
                    ctx.strokeStyle = baseMarker.lineColor || '#000000';
                    ctx.lineWidth = baseMarker.lineWidth;
                    ctx.stroke();
                }
            });
        }

        ctx.restore();
    };

    // fillColor option: plain color or Highcharts gradient object mapped
    // over the plot area.
    proto._resolveFillStyle = function (ctx, fill) {
        if (typeof fill === 'string') return fill;

        if (fill && fill.linearGradient && fill.stops) {
            var lg = fill.linearGradient;
            var gradient = ctx.createLinearGradient(
                this.plotLeft + (lg.x1 || 0) * this.plotWidth,
                this.plotTop + (lg.y1 || 0) * this.plotHeight,
                this.plotLeft + (lg.x2 || 0) * this.plotWidth,
                this.plotTop + (lg.y2 || 0) * this.plotHeight
            );

            fill.stops.forEach(function (stop) {
                gradient.addColorStop(Math.min(1, Math.max(0, stop[0])), stop[1]);
            });

            return gradient;
        }

        return '#999999';
    };

    proto._paintColumnSeries = function (ctx, series) {
        var xAxis = this.xAxis[0];
        var yAxis = this.yAxis[0];
        var seriesOptions = series.options;
        var plotSeries = this.plotOptions;
        var count = Math.max(series.points.length, 1);
        var band = this.plotWidth / count;
        var pointPadding = seriesOptions.pointPadding != null ? seriesOptions.pointPadding : (plotSeries.pointPadding != null ? plotSeries.pointPadding : 0.1);
        var groupPadding = seriesOptions.groupPadding != null ? seriesOptions.groupPadding : (plotSeries.groupPadding != null ? plotSeries.groupPadding : 0.2);
        var borderWidth = seriesOptions.borderWidth != null ? seriesOptions.borderWidth : (plotSeries.borderWidth != null ? plotSeries.borderWidth : 1);
        var borderColor = seriesOptions.borderColor || plotSeries.borderColor || '#ffffff';
        var columnWidth = Math.max(1, band * (1 - 2 * (pointPadding + groupPadding)));
        // Columns rise from 0 (the Highcharts threshold), clamped to the axis.
        var baseY = yAxis.toPixels(Math.min(Math.max(0, yAxis.min), yAxis.max));

        series.points.forEach(function (point) {
            if (point.y == null) return;

            var left = xAxis.toPixels(point.x) - columnWidth / 2;
            var valueY = yAxis.toPixels(point.y);
            var top = Math.min(valueY, baseY);
            var height = Math.abs(baseY - valueY);

            ctx.fillStyle = point.color || (typeof series.color === 'string' ? series.color : '#7cb5ec');
            ctx.fillRect(left, top, columnWidth, height);

            if (borderWidth) {
                ctx.strokeStyle = borderColor;
                ctx.lineWidth = borderWidth;
                ctx.strokeRect(left, top, columnWidth, height);
            }
        });
    };

    proto._paintShape = function (ctx, shape) {
        var attrs = shape.attrs;

        if (shape.type === 'path') {
            var d = attrs.d;
            var segments = [];

            if (typeof d === 'string') {
                segments = d.match(/[A-Za-z]|-?[\d.]+/g) || [];
            } else if (d && d.length) {
                segments = d;
            }

            if (!segments.length) return;

            ctx.beginPath();

            for (var i = 0; i < segments.length; i++) {
                var segment = segments[i];

                if (segment === 'M') {
                    ctx.moveTo(+segments[i + 1], +segments[i + 2]);
                    i += 2;
                } else if (segment === 'L') {
                    ctx.lineTo(+segments[i + 1], +segments[i + 2]);
                    i += 2;
                }
                // 'Z' on an open line path is a no-op for these shapes.
            }

            ctx.strokeStyle = attrs.stroke || '#000000';
            ctx.lineWidth = Number(attrs['stroke-width']) || 1;
            ctx.stroke();
        } else if (shape.type === 'rect') {
            ctx.save();

            if (attrs.opacity != null) ctx.globalAlpha = attrs.opacity;

            ctx.fillStyle = attrs.fill || '#000000';
            ctx.fillRect(attrs.x, attrs.y, attrs.width, attrs.height);
            ctx.restore();
        } else if (shape.type === 'image') {
            var entry = imageCache[attrs.href];

            if (!entry || !entry.loaded) return;

            if (attrs.rotation) {
                var cx = attrs.x + attrs.width / 2;
                var cy = attrs.y + attrs.height / 2;

                ctx.save();
                ctx.translate(cx, cy);
                ctx.rotate(attrs.rotation * Math.PI / 180);
                ctx.drawImage(entry.img, -attrs.width / 2, -attrs.height / 2, attrs.width, attrs.height);
                ctx.restore();
            } else {
                ctx.drawImage(entry.img, attrs.x, attrs.y, attrs.width, attrs.height);
            }
        }
    };

    proto._paintXAxis = function (ctx) {
        var xAxis0 = this.xAxis[0];
        var labelAxis = this.xAxis[1] || xAxis0;
        var labelOptions = (labelAxis.options && labelAxis.options.labels) || {};
        var opposite = labelAxis.options.opposite === true;
        var labels = labelAxis.labels || [];
        var tickLength = xAxis0.options.tickLength || 0;
        var axisY = this.plotTop + this.plotHeight + this.xAxisOffset;

        // Axis line: drawn only when a color is configured (the tide graphs
        // set lineColor; the wind/windbow charts never showed one).
        if (!opposite && xAxis0.options.lineColor && xAxis0.options.lineWidth !== 0) {
            ctx.strokeStyle = xAxis0.options.lineColor;
            ctx.lineWidth = xAxis0.options.lineWidth || 1;
            ctx.beginPath();
            ctx.moveTo(this.plotLeft, Math.round(axisY) + 0.5);
            ctx.lineTo(this.plotLeft + this.plotWidth, Math.round(axisY) + 0.5);
            ctx.stroke();
        }
        var baseFont = styleToFont(labelOptions.style, { fontSize: '11px' });
        var color = (labelOptions.style && labelOptions.style.color) || '#666666';
        var yOffset = labelOptions.y != null ? labelOptions.y : (opposite ? -8 : 20);
        var lastRight = -Infinity;
        var step = labelOptions.step || 0;

        // Tick marks (bottom axis only — mini graph).
        if (!opposite && tickLength) {
            ctx.strokeStyle = '#cccccc';
            ctx.lineWidth = 1;
            ctx.beginPath();

            for (var t = 0; t < labelAxis.tickPositions.length; t++) {
                var tickX = Math.round(xAxis0.toPixels(labelAxis.tickPositions[t])) + 0.5;

                if (tickX < this.plotLeft - 1 || tickX > this.plotLeft + this.plotWidth + 1) continue;

                ctx.moveTo(tickX, axisY);
                ctx.lineTo(tickX, axisY + tickLength);
            }

            ctx.stroke();
        }

        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = color;

        // Bottom axes auto-rotate to -45° when upright labels would collide,
        // stepping to every Nth label if even rotated ones would (Highcharts
        // autoRotation behavior; matches the windbow hourly axis).
        var rotation = labelOptions.rotation;

        if (!opposite && rotation == null && labels.length > 1) {
            ctx.font = baseFont;

            var maxLabelWidth = 0;

            for (var m = 0; m < labels.length; m++) {
                if (!labels[m].skip && labels[m].text) {
                    maxLabelWidth = Math.max(maxLabelWidth, ctx.measureText(labels[m].text).width);
                }
            }

            var slot = Math.abs(xAxis0.toPixels(labels[1].value) - xAxis0.toPixels(labels[0].value));

            if (maxLabelWidth + 6 > slot) rotation = -45;
        }

        if (rotation) {
            var fontSize = parseFloat(remToPx((labelOptions.style && labelOptions.style.fontSize) || '11px'));
            var slotWidth = labels.length > 1 ? Math.abs(xAxis0.toPixels(labels[1].value) - xAxis0.toPixels(labels[0].value)) : Infinity;
            var rotatedStep = step || Math.max(1, Math.ceil((fontSize * 2.2) / slotWidth));
            var rotatedY = axisY + (labelOptions.y != null ? labelOptions.y : 8);
            var radians = rotation * Math.PI / 180;

            ctx.font = baseFont;
            ctx.textAlign = rotation < 0 ? 'right' : 'left';

            for (var r = 0; r < labels.length; r++) {
                var rotatedLabel = labels[r];

                if (rotatedLabel.skip || !rotatedLabel.text) continue;

                if (r % rotatedStep !== 0) continue;

                var rotatedX = xAxis0.toPixels(rotatedLabel.value);

                if (rotatedX < this.plotLeft - 1 || rotatedX > this.plotLeft + this.plotWidth + 1) continue;

                ctx.save();
                ctx.translate(rotatedX, rotatedY);
                ctx.rotate(radians);
                ctx.fillText(rotatedLabel.text, 0, 0);
                ctx.restore();
            }

            return;
        }

        var showFirstX = labelAxis.options.showFirstLabel !== false;
        var showLastX = labelAxis.options.showLastLabel !== false;

        for (var i = 0; i < labels.length; i++) {
            var label = labels[i];

            if (label.skip || !label.text) continue;

            if (!showFirstX && i === 0) continue;

            if (!showLastX && i === labels.length - 1) continue;

            if (step > 1 && i % step !== 0) continue;

            var x = xAxis0.toPixels(label.value);

            if (x < this.plotLeft - 1 || x > this.plotLeft + this.plotWidth + 1) continue;

            ctx.font = label.bold ? 'bold ' + baseFont.replace(/^\w+ /, '') : baseFont;

            var width = ctx.measureText(label.text).width;

            if (x - width / 2 < lastRight + 4) continue;

            var y = opposite ? this.plotTop + yOffset : axisY + yOffset;

            ctx.fillText(label.text, x, y);
            lastRight = x + width / 2;
        }
    };

    proto._paintYAxisLabels = function (ctx) {
        var yAxis = this.yAxis[0];
        var labelOptions = (yAxis.options && yAxis.options.labels) || {};

        if (labelOptions.enabled === false) return;

        var showFirst = yAxis.options.showFirstLabel !== false;
        var showLast = yAxis.options.showLastLabel !== false;
        var labelYOffset = labelOptions.y || 0;
        var labelXOffset = labelOptions.x || 0;

        ctx.font = styleToFont(labelOptions.style, { fontSize: '11px' });
        ctx.fillStyle = (labelOptions.style && labelOptions.style.color) || '#666666';
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';

        for (var i = 0; i < yAxis.tickPositions.length; i++) {
            if (i === 0 && !showFirst) continue;

            if (i === yAxis.tickPositions.length - 1 && !showLast) continue;

            var value = yAxis.tickPositions[i];
            var text = this._yLabelText(labelOptions, value);

            ctx.fillText(text, this.plotLeft - 5 + labelXOffset, yAxis.toPixels(value) + labelYOffset);
        }
    };

    proto._paintYAxisTitle = function (ctx) {
        var yAxis = this.yAxis[0];
        var titleOptions = (yAxis.options && yAxis.options.title) || {};

        if (!titleOptions.text || titleOptions.enabled === false || titleOptions.floating) return;

        var style = titleOptions.style || {};

        ctx.save();
        ctx.translate(this.plotLeft - (this._yLabelSpace || 40) + 10, this.plotTop + this.plotHeight / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.font = styleToFont(style, { fontSize: '12px' });
        ctx.fillStyle = style.color || '#666666';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(titleOptions.text, 0, 0);
        ctx.restore();
    };

    proto._yLabelText = function (labelOptions, value) {
        if (typeof labelOptions.formatter === 'function') {
            var html = labelOptions.formatter.call({ value: value });

            return html == null ? '' : stripTags(html);
        }

        return labelOptions.format ? formatValue(labelOptions.format, value) : String(value);
    };

    proto._paintExternalYAxis = function () {
        var canvas = this._yAxisExternalCanvas;

        if (!canvas || !canvas.parentElement) return;

        var container = canvas.parentElement;
        var width = container.clientWidth || 45;
        var height = canvas.clientHeight || container.clientHeight;
        var dpr = window.devicePixelRatio || 1;

        if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);
        }

        var ctx = canvas.getContext('2d');
        var yAxis = this.yAxis[0];
        var labelOptions = (yAxis.options && yAxis.options.labels) || {};
        var titleOptions = (yAxis.options && yAxis.options.title) || {};
        var labelStyle = labelOptions.style || {};

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, height);

        ctx.font = styleToFont(labelStyle, { fontSize: '11px' });
        ctx.fillStyle = labelStyle.color || '#666666';
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';

        var labelY = labelOptions.y != null ? labelOptions.y : 3;

        for (var i = 0; i < yAxis.tickPositions.length; i++) {
            var value = yAxis.tickPositions[i];
            // The external canvas sits 2px above the chart canvas (1rem CSS top vs
            // 18px scroller padding), so shift labels to line up with gridlines.
            var y = yAxis.toPixels(value) + labelY - 3;

            if (y < 6 || y > height - 2) continue;

            ctx.fillText(String(value), width - 6, y);
        }

        if (titleOptions.text) {
            var titleStyle = titleOptions.style || {};

            ctx.save();
            ctx.translate(10, this.plotTop + this.plotHeight / 2);
            ctx.rotate(-Math.PI / 2);
            ctx.font = styleToFont(titleStyle, { fontSize: '12px' });
            ctx.fillStyle = titleStyle.color || '#666666';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(titleOptions.text, 0, 0);
            ctx.restore();
        }
    };
    return CanvasChart;
});
